<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="dialogCopy.title"
    :subtitle="dialogCopy.subtitle"
    :loading="loading"
    :save-disabled="!canSubmit || setupLoading"
    :max-width="780"
    :save-label="dialogCopy.submitLabel"
    @update:modelValue="handleDialogVisibilityChange"
    @save="submit"
  >
    <v-row>
      <v-col v-if="!isContractAppendMode" cols="12" md="6">
        <v-text-field
          v-model="form.contract_number"
          :label="$t('ncrm.deals.board.wonHandoff.fields.contractNumber')"
          required
        />
      </v-col>
      <v-col v-if="!isContractAppendMode" cols="12" md="6">
        <v-text-field
          v-model="form.contract_date"
          :label="$t('ncrm.deals.board.wonHandoff.fields.contractDate')"
          type="date"
          required
        />
      </v-col>
      <v-col cols="12">
        <v-card variant="outlined" class="pa-3">
          <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-3">
            <div>
              <div class="text-subtitle-2 ncrm-text-strong">
                {{ dialogCopy.productsTitle }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ dialogCopy.productsHint }}
              </div>
            </div>
          </div>

          <v-row dense class="mb-1">
            <v-col cols="12" md="9">
              <v-autocomplete
                v-model="form.product_id_to_add"
                :items="availableProductOptions"
                item-title="label"
                item-value="id"
                :label="$t('ncrm.deals.board.wonHandoff.fields.products')"
                clearable
                :loading="productsLoading"
                :disabled="setupLoading || loading"
                :no-filter="true"
                :menu-props="resolvedSelectMenuProps"
                @focus="ensureProductOptions"
                @update:search="handleProductSearch"
              />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-btn
                block
                color="primary"
                variant="tonal"
                :disabled="!form.product_id_to_add || setupLoading || loading"
                @click="addProduct"
              >
                <v-icon start>mdi-plus</v-icon>
                {{ $t("ncrm.deals.board.wonHandoff.actions.addProduct") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-alert
            v-if="productFeedback.message"
            :type="productFeedback.type || 'warning'"
            border="start"
            prominent
            density="comfortable"
            class="mb-3 ncrm-deal-won-handoff__feedback"
          >
            {{ productFeedback.message }}
          </v-alert>

          <v-alert
            v-if="setupLoading"
            type="info"
            density="comfortable"
          >
            {{ $t("ncrm.deals.board.wonHandoff.products.loading") }}
          </v-alert>
          <v-alert
            v-else-if="!selectedProducts.length"
            type="warning"
            density="comfortable"
          >
            {{ dialogCopy.productsEmpty }}
          </v-alert>
          <div v-else class="ncrm-deal-won-handoff__products">
            <div
              v-for="item in selectedProducts"
              :key="item.key"
              class="ncrm-deal-won-handoff__product-row"
            >
              <div class="ncrm-deal-won-handoff__product-main">
                <div class="text-body-2 ncrm-text-strong">{{ item.title }}</div>
                <div v-if="item.subtitle" class="text-caption text-medium-emphasis">
                  {{ item.subtitle }}
                </div>
              </div>
              <div class="d-flex align-center ga-2 ncrm-deal-won-handoff__product-actions">
                <v-chip size="x-small" :color="item.sourceColor">
                  {{ item.sourceLabel }}
                </v-chip>
                <v-btn
                  icon="mdi-close"
                  size="x-small"
                  variant="text"
                  color="error"
                  :disabled="loading"
                  @click="removeSelection(item)"
                />
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="form.reason"
          :label="$t('ncrm.deals.board.wonHandoff.fields.reason')"
          rows="3"
          auto-grow
          required
        />
      </v-col>
    </v-row>
  </NcrmRecordEditDialog>
</template>
<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  buildAvailableWonHandoffProductOptions,
  buildContractAppendExclusions,
  buildWonHandoffDialogCopy,
  buildWonHandoffSubmitRequest,
  DEFAULT_SELECT_MENU_PROPS,
  ensureArray,
  isWonHandoffContractAppendMode,
  normalizeWonHandoffMetaLabel,
  normalizeWonHandoffString,
  parsePositiveId,
  resolveWonHandoffInitialLineItemIds,
  resolveWonHandoffBillingMetaLabel,
  toNumber,
  toSqlDate,
} from "./wonHandoffDialogShared.mjs";
import { formatDisplayAmount } from "@/Utile/displayFormat.mjs";
export default {
  name: "NcrmDealWonHandoffDialog",
  components: {
    NcrmRecordEditDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    dealId: {
      type: [String, Number],
      default: null,
    },
    dealName: {
      type: String,
      default: "",
    },
    mode: { type: String, default: "handoff" },
    contractId: { type: [String, Number], default: null },
    contractNumber: { type: String, default: "" },
    existingContractItems: { type: Array, default: () => [] },
    targetStageId: {
      type: [String, Number],
      default: null,
    },
    dealPatch: {
      type: Object,
      default: null,
    },
    initialLineItemIds: {
      type: Array,
      default: null,
    },
    initialProductIdsToAttach: {
      type: Array,
      default: () => [],
    },
    initialContractNumber: {
      type: String,
      default: "",
    },
    initialContractDate: {
      type: String,
      default: "",
    },
    initialReason: {
      type: String,
      default: "",
    },
    selectMenuProps: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "success", "cancelled", "error"],
  data() {
    return {
      loading: false,
      setupLoading: false,
      lineItems: [],
      productsLoading: false,
      productOptions: [],
      productCatalogById: {},
      eligibleProductIds: [],
      eligibleProductOptions: [],
      productRequestSeq: 0,
      productSearchDebounceId: null,
      productFeedback: {
        type: "",
        message: "",
      },
      form: this.buildDefaultForm(),
    };
  },
  computed: {
    normalizedDealId() {
      return parsePositiveId(this.dealId);
    },
    normalizedContractId() {
      return parsePositiveId(this.contractId);
    },
    normalizedTargetStageId() {
      return parsePositiveId(this.targetStageId);
    },
    isContractAppendMode() {
      return isWonHandoffContractAppendMode(this.mode);
    },
    resolvedDealName() {
      return normalizeWonHandoffString(this.dealName) || `#${this.normalizedDealId || "-"}`;
    },
    dialogCopy() {
      return buildWonHandoffDialogCopy({
        t: this.$t.bind(this),
        isContractAppendMode: this.isContractAppendMode,
        deal: this.resolvedDealName,
        contract: normalizeWonHandoffString(this.contractNumber) || `#${this.normalizedContractId || "-"}`,
      });
    },
    contractAppendExclusions() {
      return buildContractAppendExclusions(this.existingContractItems);
    },
    resolvedSelectMenuProps() {
      return this.selectMenuProps || DEFAULT_SELECT_MENU_PROPS;
    },
    selectedProducts() {
      const selectedLineItemIds = new Set(
        ensureArray(this.form?.selected_line_item_ids).map((value) => parsePositiveId(value)).filter(Boolean)
      );
      const selectedPendingProductIds = Array.from(
        new Set(
          ensureArray(this.form?.product_ids_to_attach)
            .map((value) => parsePositiveId(value))
            .filter(Boolean)
        )
      );
      const existingSelections = ensureArray(this.lineItems)
        .filter((item) => selectedLineItemIds.has(parsePositiveId(item?.id)))
        .map((item) => {
          const lineItemId = parsePositiveId(item?.id);
          const productId = parsePositiveId(item?.product_id);
          const title =
            normalizeWonHandoffString(item?.product_name) ||
            normalizeWonHandoffString(item?.name_snapshot) ||
            this.resolveProductName(productId) ||
            (lineItemId ? `#${lineItemId}` : `#${productId || "-"}`);
          const amount = this.formatCurrency(item?.total_amount);
          const currency = normalizeWonHandoffString(item?.currency);
          return {
            key: `line-item-${lineItemId}`,
            kind: "line_item",
            lineItemId,
            productId,
            title,
            subtitle: [amount !== "-" ? amount : "", currency].filter(Boolean).join(" • "),
            sourceLabel: this.$t("ncrm.deals.board.wonHandoff.products.sources.existing"),
            sourceColor: "info",
          };
        });
      const pendingSelections = selectedPendingProductIds.map((productId) => {
        const product = this.productCatalogById?.[productId] || this.findEligibleProductOption(productId) || {};
        const title = normalizeWonHandoffString(product?.name || product?.label) || `#${productId}`;
        const amount = this.formatCurrency(product?.default_unit_price);
        const currency = normalizeWonHandoffString(product?.default_currency);
        return {
          key: `product-${productId}`,
          kind: "product",
          lineItemId: null,
          productId,
          title,
          subtitle: [amount !== "-" ? amount : "", currency].filter(Boolean).join(" • "),
          sourceLabel: this.$t("ncrm.deals.board.wonHandoff.products.sources.added"),
          sourceColor: "primary",
          };
        });
      return [...existingSelections, ...pendingSelections];
    },
    availableProductOptions() {
      return buildAvailableWonHandoffProductOptions({
        form: this.form,
        lineItems: this.lineItems,
        productOptions: this.productOptions,
        eligibleProductOptions: this.eligibleProductOptions,
        excludedProductIds: this.contractAppendExclusions?.productIds || this.excludedProductIds,
      });
    },
    canSubmit() {
      const contractNumber = normalizeWonHandoffString(this.form?.contract_number);
      const contractDate = normalizeWonHandoffString(this.form?.contract_date);
      const reason = normalizeWonHandoffString(this.form?.reason);
      return Boolean(
        (this.isContractAppendMode || (contractNumber && contractDate)) &&
        reason &&
        this.selectedProducts.length &&
        !this.loading &&
        !this.setupLoading
      );
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (value) {
          this.initializeDialog();
          return;
        }
        this.resetDialogState();
      },
    },
  },
  beforeUnmount() {
    if (this.productSearchDebounceId) {
      clearTimeout(this.productSearchDebounceId);
    }
  },
  methods: {
    buildDefaultForm() {
      return {
        contract_number: normalizeWonHandoffString(this.initialContractNumber),
        contract_date: normalizeWonHandoffString(this.initialContractDate) || toSqlDate(new Date()),
        reason: normalizeWonHandoffString(this.initialReason),
        selected_line_item_ids: Array.isArray(this.initialLineItemIds)
          ? ensureArray(this.initialLineItemIds).map((value) => parsePositiveId(value)).filter(Boolean)
          : [],
        product_ids_to_attach: ensureArray(this.initialProductIdsToAttach)
          .map((value) => parsePositiveId(value))
          .filter(Boolean),
        product_id_to_add: null,
      };
    },
    resetDialogState() {
      this.loading = false;
      this.setupLoading = false;
      this.lineItems = [];
      this.productsLoading = false;
      this.productOptions = [];
      this.productCatalogById = {};
      this.eligibleProductIds = [];
      this.eligibleProductOptions = [];
      this.clearProductFeedback();
      this.form = this.buildDefaultForm();
    },
    handleDialogVisibilityChange(value) {
      const nextValue = Boolean(value);
      this.$emit("update:modelValue", nextValue);
      if (!nextValue) {
        this.$emit("cancelled");
      }
    },
    emitError(message, color = "error") {
      this.$emit("error", {
        message: normalizeWonHandoffString(message),
        color: normalizeWonHandoffString(color) || "error",
      });
    },
    setProductFeedback(message = "", type = "warning") {
      this.productFeedback = {
        type,
        message: normalizeWonHandoffString(message),
      };
    },
    clearProductFeedback() {
      this.setProductFeedback("", "");
    },
    resolveProductName(productId, fallback = "") {
      const normalizedProductId = parsePositiveId(productId);
      const product = normalizedProductId ? this.productCatalogById?.[normalizedProductId] : null;
      return (
        normalizeWonHandoffString(product?.name) ||
        normalizeWonHandoffString(this.findEligibleProductOption(normalizedProductId)?.label) ||
        normalizeWonHandoffString(fallback) ||
        (normalizedProductId ? `#${normalizedProductId}` : "")
      );
    },
    findEligibleProductOption(productId) {
      const normalizedProductId = parsePositiveId(productId);
      if (!normalizedProductId) return null;
      return ensureArray(this.eligibleProductOptions).find((item) => parsePositiveId(item?.id) === normalizedProductId) || null;
    },
    findUnselectedLineItemsByProductId(productId) {
      const normalizedProductId = parsePositiveId(productId);
      if (!normalizedProductId) return [];
      const selectedLineItemIds = new Set(
        ensureArray(this.form?.selected_line_item_ids).map((value) => parsePositiveId(value)).filter(Boolean)
      );
      return ensureArray(this.lineItems).filter((item) => {
        const lineItemId = parsePositiveId(item?.id);
        if (!lineItemId || selectedLineItemIds.has(lineItemId)) return false;
        return parsePositiveId(item?.product_id) === normalizedProductId;
      });
    },
    formatCurrency(value) {
      const parsed = toNumber(value, null);
      if (parsed === null) return "-";
      return formatDisplayAmount(parsed);
    },
    isProductEligible(productId) {
      const normalizedProductId = parsePositiveId(productId);
      if (!normalizedProductId) return false;
      return new Set(
        ensureArray(this.eligibleProductIds).map((value) => parsePositiveId(value)).filter(Boolean)
      ).has(normalizedProductId);
    },
    async fetchEligibleProductIds() {
      const { data } = await axios.get("/ncrm/onboarding/templates", {
        params: {
          include_inactive: false,
        },
      });
      const byProductId = new Map();
      for (const item of ensureArray(data?.items)) {
        const productId = parsePositiveId(item?.product_id);
        if (!productId || byProductId.has(productId)) continue;
        const productName =
          normalizeWonHandoffString(item?.product_name) ||
          normalizeWonHandoffString(item?.name) ||
          `#${productId}`;
        byProductId.set(productId, {
          id: productId,
          label: productName,
        });
      }
      const productIds = Array.from(byProductId.keys());
      this.eligibleProductIds = productIds;
      this.eligibleProductOptions = Array.from(byProductId.values());
      return productIds;
    },
    applyInitialLineItemSelection(items = []) {
      this.form.selected_line_item_ids = Array.from(new Set(resolveWonHandoffInitialLineItemIds({
        items,
        initialLineItemIds: this.initialLineItemIds,
        isContractAppendMode: this.isContractAppendMode,
      })));
    },
    syncSelectedLineItemsWithEligibility() {
      const eligibleProductIds = new Set(
        ensureArray(this.eligibleProductIds).map((value) => parsePositiveId(value)).filter(Boolean)
      );
      const lineItems = ensureArray(this.lineItems);
      const selectedLineItemIds = new Set(
        ensureArray(this.form.selected_line_item_ids).map((value) => parsePositiveId(value)).filter(Boolean)
      );
      const eligibleLineItemIds = lineItems
        .filter((item) => {
          const lineItemId = parsePositiveId(item?.id);
          if (!lineItemId || !selectedLineItemIds.has(lineItemId)) return false;
          return eligibleProductIds.has(parsePositiveId(item?.product_id));
        })
        .map((item) => parsePositiveId(item?.id))
        .filter(Boolean);
      const removedTitles = lineItems
        .filter((item) => {
          const lineItemId = parsePositiveId(item?.id);
          if (!lineItemId || !selectedLineItemIds.has(lineItemId)) return false;
          return !eligibleProductIds.has(parsePositiveId(item?.product_id));
        })
        .map((item) => this.resolveProductName(item?.product_id, item?.product_name || item?.name_snapshot))
        .filter(Boolean);

      this.form.selected_line_item_ids = eligibleLineItemIds;
      if (removedTitles.length) {
        this.setProductFeedback(
          this.$t("ncrm.deals.board.wonHandoff.products.ineligibleExisting", {
            products: removedTitles.join(", "),
          }),
          "warning"
        );
      } else {
        this.clearProductFeedback();
      }
    },
    async fetchLineItems(dealId) {
      const normalizedDealId = parsePositiveId(dealId);
      if (!normalizedDealId) {
        this.lineItems = [];
        this.form.selected_line_item_ids = [];
        return [];
      }
      const { data } = await axios.get(`/ncrm/deals/${normalizedDealId}/line-items`);
      const excludedLineItemIds = this.contractAppendExclusions.lineItemIds;
      const items = (Array.isArray(data?.items) ? data.items : []).filter((item) => {
        const lineItemId = parsePositiveId(item?.id);
        return !lineItemId || !excludedLineItemIds.has(lineItemId);
      });
      this.lineItems = items;
      this.applyInitialLineItemSelection(items);
      return items;
    },
    handleProductSearch(search) {
      if (this.productSearchDebounceId) {
        clearTimeout(this.productSearchDebounceId);
      }
      this.productSearchDebounceId = setTimeout(() => {
        this.fetchProductOptions(search).catch((error) => {
          console.error("[ncrm-deal-won-handoff] search products error", error);
        });
      }, 220);
    },
    async fetchProductOptions(search = "") {
      const requestSeq = ++this.productRequestSeq;
      this.productsLoading = true;
      try {
        const normalizedSearch = normalizeWonHandoffString(search);
        const { data } = await axios.get("/ncrm/products", {
          params: {
            search: normalizedSearch || undefined,
            limit: 50,
            is_active: true,
          },
        });
        if (requestSeq !== this.productRequestSeq) return [];
        const items = Array.isArray(data?.items) ? data.items : [];
        const nextCatalogById = { ...this.productCatalogById };
        this.productOptions = items.map((item) => {
          const id = parsePositiveId(item?.id);
          const name = normalizeWonHandoffString(item?.name) || `#${id}`;
          const productType = normalizeWonHandoffMetaLabel(item?.product_type);
          const billingPeriod = resolveWonHandoffBillingMetaLabel(item?.product_type, item?.billing_period);
          const suffix = [productType, billingPeriod].filter(Boolean).join(" • ");
          const label = suffix ? `${name} (${suffix})` : name;
          if (id) {
            nextCatalogById[id] = item;
          }
          return {
            id,
            label,
          };
        });
        this.productCatalogById = nextCatalogById;
        return this.productOptions;
      } finally {
        if (requestSeq === this.productRequestSeq) {
          this.productsLoading = false;
        }
      }
    },
    async ensureProductOptions() {
      if (this.setupLoading || this.loading) return;
      if (this.availableProductOptions.length) return;
      const needsCatalog = !ensureArray(this.productOptions).length;
      const needsEligible = !ensureArray(this.eligibleProductIds).length;
      if (!needsCatalog && !needsEligible) return;

      const requests = [];
      if (needsCatalog) {
        requests.push(this.fetchProductOptions(""));
      }
      if (needsEligible) {
        requests.push(this.fetchEligibleProductIds());
      }

      const settled = await Promise.allSettled(requests);
      const hasRejected = settled.some((item) => item.status === "rejected");
      if (hasRejected && !this.availableProductOptions.length) {
        this.emitError(this.$t("ncrm.deals.board.messages.handoffProductsLoadError"), "warning");
      }
    },
    addProduct() {
      const productId = parsePositiveId(this.form?.product_id_to_add);
      if (!productId) return;
      if (!this.isProductEligible(productId)) {
        this.setProductFeedback(
          this.$t("ncrm.deals.board.wonHandoff.products.ineligibleAdd", {
            product: this.resolveProductName(productId),
          }),
          "error"
        );
        return;
      }
      const unselectedLineItems = this.findUnselectedLineItemsByProductId(productId);
      if (unselectedLineItems.length) {
        const currentLineItemIds = new Set(
          ensureArray(this.form?.selected_line_item_ids).map((value) => parsePositiveId(value)).filter(Boolean)
        );
        currentLineItemIds.add(parsePositiveId(unselectedLineItems[0]?.id));
        this.form.selected_line_item_ids = Array.from(currentLineItemIds);
        this.form.product_id_to_add = null;
        this.clearProductFeedback();
        return;
      }
      const current = new Set(
        ensureArray(this.form?.product_ids_to_attach).map((value) => parsePositiveId(value)).filter(Boolean)
      );
      current.add(productId);
      this.form.product_ids_to_attach = Array.from(current);
      this.form.product_id_to_add = null;
      this.clearProductFeedback();
    },
    removeSelection(item = {}) {
      if (item?.kind === "line_item") {
        const lineItemId = parsePositiveId(item?.lineItemId);
        if (!lineItemId) return;
        this.form.selected_line_item_ids = ensureArray(this.form?.selected_line_item_ids)
          .map((value) => parsePositiveId(value))
          .filter((value) => value && value !== lineItemId);
        return;
      }
      const productId = parsePositiveId(item?.productId);
      if (!productId) return;
      this.form.product_ids_to_attach = ensureArray(this.form?.product_ids_to_attach)
        .map((value) => parsePositiveId(value))
        .filter((value) => value && value !== productId);
    },
    normalizeDealPatch() {
      if (!this.dealPatch || typeof this.dealPatch !== "object" || Array.isArray(this.dealPatch)) {
        return null;
      }
      const entries = Object.entries(this.dealPatch).filter(([, value]) => value !== undefined);
      return entries.length ? Object.fromEntries(entries) : null;
    },
    async initializeDialog() {
      const dealId = this.normalizedDealId;
      if (!dealId || (this.isContractAppendMode && !this.normalizedContractId)) return;

      this.form = this.buildDefaultForm();
      this.lineItems = [];
      this.eligibleProductIds = [];
      this.eligibleProductOptions = [];
      this.clearProductFeedback();
      this.setupLoading = true;

      try {
        const [lineItemsResult, productOptionsResult, eligibleProductsResult] = await Promise.allSettled([
          this.fetchLineItems(dealId),
          this.fetchProductOptions(""),
          this.fetchEligibleProductIds(),
        ]);
        if (lineItemsResult.status === "rejected") {
          throw lineItemsResult.reason;
        }
        if (eligibleProductsResult.status === "rejected") {
          throw eligibleProductsResult.reason;
        }
        this.syncSelectedLineItemsWithEligibility();
        if (productOptionsResult.status === "rejected") {
          this.emitError(
            productOptionsResult.reason?.response?.data?.error ||
              this.$t("ncrm.deals.board.messages.handoffProductsLoadError"),
            "warning"
          );
        }
      } catch (error) {
        console.error("[ncrm-deal-won-handoff] open dialog error", error);
        this.emitError(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.handoffError"),
          "error"
        );
        this.handleDialogVisibilityChange(false);
      } finally {
        this.setupLoading = false;
      }
    },
    async submit() {
      const dealId = this.normalizedDealId;
      const targetStageId = this.normalizedTargetStageId;
      const contractNumber = normalizeWonHandoffString(this.form?.contract_number);
      const contractDate = normalizeWonHandoffString(this.form?.contract_date);
      const reason = normalizeWonHandoffString(this.form?.reason);
      const selectedLineItemIds = Array.from(
        new Set(
          ensureArray(this.form?.selected_line_item_ids)
            .map((value) => parsePositiveId(value))
            .filter(Boolean)
        )
      );
      const productIdsToAttach = Array.from(
        new Set(
          ensureArray(this.form?.product_ids_to_attach)
            .map((value) => parsePositiveId(value))
            .filter(Boolean)
        )
      );
      const eligibleProductIds = new Set(
        ensureArray(this.eligibleProductIds).map((value) => parsePositiveId(value)).filter(Boolean)
      );

      const missingContractFields = !this.isContractAppendMode && (!contractNumber || !contractDate);
      if (!dealId || missingContractFields || !reason || (this.isContractAppendMode && !this.normalizedContractId)) {
        this.emitError(
          this.isContractAppendMode
            ? this.$t("ncrm.contracts.messages.productAddValidation")
            : this.$t("ncrm.deals.board.messages.handoffValidation"),
          "warning"
        );
        return;
      }

      const invalidSelectedProduct = ensureArray(this.lineItems).find(
        (item) =>
          selectedLineItemIds.includes(parsePositiveId(item?.id)) &&
          !eligibleProductIds.has(parsePositiveId(item?.product_id))
      );
      if (invalidSelectedProduct) {
        this.setProductFeedback(
          this.$t("ncrm.deals.board.wonHandoff.products.ineligibleExisting", {
            products: this.resolveProductName(
              invalidSelectedProduct?.product_id,
              invalidSelectedProduct?.product_name || invalidSelectedProduct?.name_snapshot
            ),
          }),
          "error"
        );
        return;
      }

      const invalidAddedProductId = productIdsToAttach.find((productId) => !eligibleProductIds.has(productId));
      if (invalidAddedProductId) {
        this.setProductFeedback(
          this.$t("ncrm.deals.board.wonHandoff.products.ineligibleAdd", {
            product: this.resolveProductName(invalidAddedProductId),
          }),
          "error"
        );
        return;
      }

      if (!selectedLineItemIds.length && !productIdsToAttach.length) {
        this.emitError(this.$t("ncrm.deals.board.messages.handoffProductsRequired"), "warning");
        return;
      }

      this.loading = true;
      try {
        const request = buildWonHandoffSubmitRequest({
          isContractAppendMode: this.isContractAppendMode,
          contractId: this.normalizedContractId,
          dealId,
          targetStageId,
          contractNumber,
          contractDate,
          reason,
          selectedLineItemIds,
          productIdsToAttach,
          dealPatch: this.normalizeDealPatch(),
        });
        const response = await axios.post(request.url, request.payload);
        this.$emit("success", { payload: request.payload, response });
        this.$emit("update:modelValue", false);
      } catch (error) {
        console.error("[ncrm-deal-won-handoff] submit error", error);
        this.emitError(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.handoffError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-deal-won-handoff__products {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ncrm-deal-won-handoff__product-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px dashed rgba(var(--v-theme-on-surface), 0.16);
}

.ncrm-deal-won-handoff__product-row:first-child {
  padding-top: 0;
  border-top: none;
}

.ncrm-deal-won-handoff__product-main {
  min-width: 0;
  flex: 1 1 auto;
}

.ncrm-deal-won-handoff__product-actions {
  flex: 0 0 auto;
}

.ncrm-deal-won-handoff__feedback {
  border-width: 1px;
}

@media (max-width: 960px) {
  .ncrm-deal-won-handoff__product-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ncrm-deal-won-handoff__product-actions {
    justify-content: space-between;
  }
}
</style>
